import { type InferenceModel } from '@qdrant/qdrant-cloud-public-api/booking/v1';
import {
  type Cluster,
  ClusterBackupSupportStatus,
  ClusterPhase,
  type ClusterValid,
} from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { type ClusterNodeUsageMetrics } from '@qdrant/qdrant-cloud-public-api/monitoring/v1';
import { CLOUD_PROVIDER_MAP } from '@/components/Clusters/constants';
import { type DeepOmitProtobufInternals } from '@/connect-query';
import { reportError } from '@/helpers/errors/report';
import { isAtLeastVersion } from '@/utils/helpers';
import { toCamelCase } from '@/utils/string-formatters';
import { type DeepPartial } from '@/utils/types';

export type MetricType = Exclude<keyof DeepOmitProtobufInternals<ClusterNodeUsageMetrics>, 'nodeId'>;

export function getNodeNumber(clusterId: string, nodeId: string): string {
  return nodeId.replace(`qdrant-${clusterId}-`, '');
}

export const isClusterHybridCloud = (cluster?: Pick<ClusterValid, 'cloudProviderId'>) =>
  cluster?.cloudProviderId === CLOUD_PROVIDER_MAP.HYBRID;

/**
 * Whether a hybrid cloud cluster is eligible for force deletion.
 *
 * Force delete is only offered once a normal delete has hung: the cluster is marked for deletion
 * (surfaced as the "Deleting" chip via `deletedAt`, see `ClusterState`) AND its region is
 * unreachable. When the cloud-agent stops reporting, the backend derives the dedicated
 * `ClusterPhase.UNKNOWN` phase, this covers both a previously-healthy cluster
 * and one stranded while still creating. Older backends collapsed it to `ClusterPhase.UNSPECIFIED`,
 * so we accept both. We do NOT accept `CREATING`: a cluster genuinely creating on a reachable
 * region is not yet stranded. Only hybrid cloud clusters can strand this way, so the check is
 * scoped to them.
 */
export const isClusterForceDeletable = (cluster?: ClusterValid | null): boolean =>
  Boolean(
    cluster &&
    isClusterHybridCloud(cluster) &&
    cluster.deletedAt &&
    (cluster.state?.phase === ClusterPhase.UNKNOWN || cluster.state?.phase === ClusterPhase.UNSPECIFIED),
  );

export const getNonHybridClusters = (clusters: ClusterValid[] = []) =>
  clusters.filter((cluster) => !isClusterHybridCloud(cluster));

export const getClusterEndpoint = (cluster?: ClusterValid) => cluster?.state?.endpoint;

type ClusterProtocol = 'http' | 'https';

export type ClusterEndpointData = {
  hostname: string;
  protocol?: ClusterProtocol;
  restPort?: number;
  grpcPort?: number;
};

function isClusterProtocol(protocol: string): protocol is ClusterProtocol {
  return ['http', 'https'].includes(protocol);
}

/**
 * Normalizes a raw protocol string to a supported cluster protocol.
 * Returns undefined when the protocol is not http or https.
 */
export const normalizeClusterProtocol = (rawProtocol?: string | null): ClusterProtocol | undefined => {
  if (!rawProtocol) {
    return undefined;
  }

  const cleaned = rawProtocol.replace(':', '');
  return isClusterProtocol(cleaned) ? cleaned : undefined;
};

/**
 * Parses the cluster endpoint URL to extract connection details
 * @param cluster - The cluster to extract connection details from
 * @returns Object containing hostname, protocol, restPort, and grpcPort
 */
export const parseClusterEndpoint = (cluster: ClusterValid): ClusterEndpointData => {
  const endpointUrl = getClusterEndpoint(cluster);
  if (endpointUrl) {
    const url = URL.parse(endpointUrl.url);
    if (url) {
      const protocol = normalizeClusterProtocol(url.protocol);
      return {
        hostname: url.hostname,
        protocol,
        restPort: endpointUrl.restPort,
        grpcPort: endpointUrl.grpcPort,
      };
    }

    reportError(new Error('Invalid cluster endpoint'), {
      domain: 'cluster',
      level: 'fatal',
      extra: { clusterId: cluster.id, clusterStateEndpoint: endpointUrl },
    });
  }

  return { hostname: '<waiting-for-cluster-host>' };
};

export const isClusterHealthy = (cluster: ClusterValid | null) => cluster?.state?.phase === ClusterPhase.HEALTHY;

/**
 * For now we consider a cluster as not reachable if it is in the 'Creating' or 'Updating' state.
 * But this logic should be extended to consider other states that might indicate that the cluster is not reachable.
 * Warn: This doesn't mean that cluster is exactly not reachable, but we should prevent too eager error reports and warnings.
 */
export const isClusterUndergoingChanges = (cluster: ClusterValid | null) =>
  Boolean(cluster?.state?.phase && [ClusterPhase.CREATING, ClusterPhase.UPDATING].includes(cluster.state.phase));

export const isClusterCreating = (cluster: ClusterValid | null) =>
  Boolean(cluster?.state?.phase && [ClusterPhase.CREATING].includes(cluster.state.phase));

export const hasClusterRbacEnabled = (cluster?: ClusterValid) => cluster?.state?.jwtRbac ?? false;

export const hasInferenceEnabled = (cluster?: ClusterValid) =>
  cluster?.configuration?.databaseConfiguration?.inference?.enabled ?? false;

/**
 * Inference feature availability is given by the presence of inference models.
 * If inference models are present, it means that the inference feature is available and can be enabled.
 */
export const isInferenceAvailable = (inferenceModels?: InferenceModel[]) =>
  Boolean(inferenceModels && inferenceModels.length > 0);

export const MIN_CLUSTER_VERSION_FOR_INFERENCE = '1.13.6';
export const doesClusterSupportInference = (cluster: ClusterValid) =>
  isAtLeastVersion(MIN_CLUSTER_VERSION_FOR_INFERENCE, cluster.configuration?.version);

export const MIN_CLUSTER_VERSION_FOR_AUDIT_LOGGING = '1.17.0';
export const doesClusterSupportAuditLogging = (cluster: ClusterValid) =>
  isAtLeastVersion(MIN_CLUSTER_VERSION_FOR_AUDIT_LOGGING, cluster.configuration?.version);

const MIN_CLUSTER_VERSION_FOR_AUDIT_LOG_OUTPUT = '1.20.0';
export const doesClusterSupportAuditLogOutput = (cluster: ClusterValid) =>
  isAtLeastVersion(MIN_CLUSTER_VERSION_FOR_AUDIT_LOG_OUTPUT, cluster.configuration?.version);

export const isClusterSuspended = (cluster: ClusterValid) =>
  cluster?.state?.phase === ClusterPhase.SUSPENDED ||
  (cluster?.state === null && cluster?.configuration?.numberOfNodes === 0);

export const isClusterActionable = (cluster: ClusterValid) => cluster.state?.phase !== ClusterPhase.MANUAL_MAINTENANCE;

// Only clusters with explicitly SUPPORTED status — UNSPECIFIED and NOT_SUPPORTED are excluded.
export const getClustersWithBackupCapability = (clusters: ClusterValid[]) =>
  clusters.filter((cluster) => cluster.state?.capabilities?.backup?.status === ClusterBackupSupportStatus.SUPPORTED);

/** What a dot path into a cluster message can land on: a leaf, or the message nesting the next step. */
type ClusterFieldValue =
  | bigint
  | boolean
  | ClusterFieldValue[]
  | null
  | number
  | string
  | undefined
  | { [field: string]: ClusterFieldValue };

export const getValueAtPath = (
  cluster: DeepPartial<DeepOmitProtobufInternals<Cluster>> | undefined,
  path: string,
): ClusterFieldValue =>
  path
    .split('.')
    .reduce<ClusterFieldValue>(
      (value, segment) =>
        value && typeof value === 'object' && !Array.isArray(value) ? value[toCamelCase(segment)] : undefined,
      cluster as ClusterFieldValue,
    );
